var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
import { getStartOfTimeframe, getTimeframeMilliseconds } from "./date.js";
import { AvailableTimeframes } from "./constant.js";
import {
  CandleValueType,
  CandleComputedType,
  OhlcvIndex,
  OrderType,
  PositionOrderType,
  PositionType,
  OrderMode,
  InvestMode,
  OrderSideCcxt,
  isCandleComputedType
} from "./enum.js";
const getOhlcvIndex = /* @__PURE__ */ __name((key) => {
  return {
    [CandleValueType.OPEN]: OhlcvIndex.OPEN,
    [CandleValueType.HIGH]: OhlcvIndex.HIGH,
    [CandleValueType.LOW]: OhlcvIndex.LOW,
    [CandleValueType.CLOSE]: OhlcvIndex.CLOSE,
    [CandleValueType.VOLUME]: OhlcvIndex.VOLUME,
    [CandleValueType.TAKER_BUY_VOLUME]: OhlcvIndex.TAKER_BUY_VOLUME
  }[key];
}, "getOhlcvIndex");
function getCandleComputedValue(ohlcv, source) {
  if (!ohlcv || !ohlcv[OhlcvIndex.CLOSE] || !ohlcv[OhlcvIndex.OPEN] || !ohlcv[OhlcvIndex.HIGH] || !ohlcv[OhlcvIndex.LOW]) {
    return void 0;
  }
  return {
    [CandleComputedType.CLOSE_OPEN_DELTA]: ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.OPEN],
    [CandleComputedType.CLOSE_OPEN_DELTA_ABSOLUTE]: Math.abs(ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.OPEN]),
    [CandleComputedType.CLOSE_OPEN_DELTA_PERCENT]: (ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.OPEN]) / ohlcv[OhlcvIndex.OPEN] * 100,
    [CandleComputedType.CLOSE_OPEN_DELTA_PERCENT_ABSOLUTE]: Math.abs(
      (ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.OPEN]) / ohlcv[OhlcvIndex.OPEN] * 100
    ),
    [CandleComputedType.HIGH_LOW_DELTA]: ohlcv[OhlcvIndex.HIGH] - ohlcv[OhlcvIndex.LOW],
    [CandleComputedType.HIGH_LOW_DELTA_PERCENT]: (ohlcv[OhlcvIndex.HIGH] - ohlcv[OhlcvIndex.LOW]) / ohlcv[OhlcvIndex.LOW] * 100,
    [CandleComputedType.CLOSE_LOW_DELTA]: ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.LOW],
    [CandleComputedType.CLOSE_LOW_DELTA_PERCENT]: (ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.LOW]) / ohlcv[OhlcvIndex.LOW] * 100,
    [CandleComputedType.CLOSE_HIGH_DELTA]: ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.HIGH],
    [CandleComputedType.CLOSE_HIGH_DELTA_PERCENT]: (ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.HIGH]) / ohlcv[OhlcvIndex.CLOSE] * 100,
    [CandleComputedType.HL]: (ohlcv[OhlcvIndex.HIGH] + ohlcv[OhlcvIndex.LOW]) / 2,
    [CandleComputedType.HLC]: (ohlcv[OhlcvIndex.HIGH] + ohlcv[OhlcvIndex.LOW] + ohlcv[OhlcvIndex.CLOSE]) / 3,
    [CandleComputedType.HLCC]: (ohlcv[OhlcvIndex.HIGH] + ohlcv[OhlcvIndex.LOW] + ohlcv[OhlcvIndex.CLOSE] * 2) / 4,
    [CandleComputedType.OHLC]: (ohlcv[OhlcvIndex.OPEN] + ohlcv[OhlcvIndex.HIGH] + ohlcv[OhlcvIndex.LOW] + ohlcv[OhlcvIndex.CLOSE]) / 4
  }[source];
}
__name(getCandleComputedValue, "getCandleComputedValue");
const getCandleValue = /* @__PURE__ */ __name((ohlcv, source) => {
  if (isCandleComputedType(source)) {
    return getCandleComputedValue(ohlcv, source) ?? 0;
  } else {
    return Number(ohlcv[getOhlcvIndex(source)]);
  }
}, "getCandleValue");
const isEnterType = /* @__PURE__ */ __name((someType) => someType.includes(OrderType.ENTER), "isEnterType");
const isExitType = /* @__PURE__ */ __name((someType) => someType.includes(OrderType.EXIT), "isExitType");
const isLongType = /* @__PURE__ */ __name((someType) => !!someType && [PositionOrderType.LONG_ENTER, PositionOrderType.LONG_EXIT].includes(someType), "isLongType");
const isShortType = /* @__PURE__ */ __name((someType) => !!someType && [PositionOrderType.SHORT_ENTER, PositionOrderType.SHORT_EXIT].includes(someType), "isShortType");
const getEnterPositionOrderType = /* @__PURE__ */ __name((positionType) => (
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  `${positionType}_${OrderType.ENTER}`
), "getEnterPositionOrderType");
const getExitPositionOrderType = /* @__PURE__ */ __name((positionType) => (
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  `${positionType}_${OrderType.EXIT}`
), "getExitPositionOrderType");
const getPositionType = /* @__PURE__ */ __name((someType) => isLongType(someType) ? PositionType.LONG : PositionType.SHORT, "getPositionType");
const getOrderSide = /* @__PURE__ */ __name((positionOrderType) => ({
  [PositionOrderType.LONG_ENTER]: OrderSideCcxt.BUY,
  [PositionOrderType.SHORT_ENTER]: OrderSideCcxt.SELL,
  [PositionOrderType.LONG_EXIT]: OrderSideCcxt.SELL,
  [PositionOrderType.SHORT_EXIT]: OrderSideCcxt.BUY
})[positionOrderType], "getOrderSide");
function isPositionOrderType(value) {
  return Object.values(PositionOrderType).includes(value);
}
__name(isPositionOrderType, "isPositionOrderType");
const calcPnl = /* @__PURE__ */ __name((type, contracts, entryPrice, currentPrice) => {
  return (type === PositionType.LONG ? currentPrice - entryPrice : entryPrice - currentPrice) * contracts;
}, "calcPnl");
const calcRoe = /* @__PURE__ */ __name((pnl, contracts, entryPrice, leverage) => {
  const entryQuoteAmount = entryPrice * contracts / leverage;
  return pnl / entryQuoteAmount * 100;
}, "calcRoe");
const sortTimeframes = /* @__PURE__ */ __name((timeframes) => {
  return timeframes.map((tf) => ({ timeframe: tf, seconds: getTimeframeMilliseconds(tf) })).sort((a, b) => a.seconds - b.seconds).map(({ timeframe }) => timeframe);
}, "sortTimeframes");
function findDefaultOverlappingStep(cutSteps) {
  for (let i = cutSteps.length - 1; i >= 0; i--) {
    const { down, up } = cutSteps[i];
    if (down.change <= 0 && 0 <= up.change) {
      return i;
    }
  }
  return -1;
}
__name(findDefaultOverlappingStep, "findDefaultOverlappingStep");
function convertOhlcvToTicker(symbol, ohlcv) {
  const change = ohlcv[OhlcvIndex.CLOSE] - ohlcv[OhlcvIndex.OPEN];
  return {
    symbol,
    open: ohlcv[OhlcvIndex.OPEN],
    high: ohlcv[OhlcvIndex.HIGH],
    low: ohlcv[OhlcvIndex.LOW],
    close: ohlcv[OhlcvIndex.CLOSE],
    change,
    percentage: change / ohlcv[OhlcvIndex.OPEN] * 100,
    baseVolume: ohlcv[OhlcvIndex.VOLUME],
    quoteVolume: ohlcv[OhlcvIndex.QUOTE_VOLUME]
  };
}
__name(convertOhlcvToTicker, "convertOhlcvToTicker");
function convertTickerToOhlcv(ticker, timeframe) {
  const close = ticker.close || 0;
  return [getStartOfTimeframe(ticker.timestamp || 0, timeframe), close, close, close, close, 0, 0, 0];
}
__name(convertTickerToOhlcv, "convertTickerToOhlcv");
function updateTickerPrice(ticker, price) {
  const open = ticker.open || price;
  const change = price - (ticker.open || price);
  const high = Math.max(price, ticker.high || 0);
  const low = Math.min(price, ticker.low || 0);
  return { ...ticker, high, low, close: price, change, percentage: change / open * 100 };
}
__name(updateTickerPrice, "updateTickerPrice");
function isEnterable({
  investMode,
  enterPositionType,
  // allowReenter = false,
  positions
}) {
  const position = positions == null ? void 0 : positions[enterPositionType];
  const oppositePositionType = getOppositePositionType(enterPositionType);
  const oppositePosition = positions == null ? void 0 : positions[oppositePositionType];
  if (oppositePosition && !oppositePosition.hedged) {
    return false;
  }
  if (!position) {
    return true;
  }
  if (position.orderMode === OrderMode.BACK_SIGNAL) {
    return "afterExit";
  }
  if (investMode === InvestMode.REAL && position.orderMode === OrderMode.SIMULATION) {
    return "afterExit";
  }
  if (investMode == InvestMode.SIMULATION && position.orderMode === InvestMode.REAL) {
    return false;
  }
  return "reenter";
}
__name(isEnterable, "isEnterable");
function getShortSymbolName(fullName) {
  if (!fullName) return "";
  if (fullName === "") {
    return "";
  } else {
    return fullName.split("/")[0] + (fullName.split("-").length > 1 ? "-" + fullName.split("-")[1] : "");
  }
}
__name(getShortSymbolName, "getShortSymbolName");
const isMarketStatus = /* @__PURE__ */ __name((market) => {
  return !!market && "marginMode" in market;
}, "isMarketStatus");
function converToUnifiedRecord(selected, ticker, market) {
  const { symbol } = ticker;
  const price = (ticker == null ? void 0 : ticker.close) || (isMarketStatus(market) ? market.price : 0);
  const high = (ticker == null ? void 0 : ticker.high) || 0;
  const low = (ticker == null ? void 0 : ticker.low) || 0;
  const fromHigh = high > 0 ? (price - high) / high * 100 : 0;
  const fromLow = low > 0 ? (price - low) / low * 100 : 0;
  const highLowChange = Math.abs(fromHigh) + Math.abs(fromLow);
  return {
    // 공통 필드
    id: symbol,
    symbol,
    shortSymbol: getShortSymbolName(symbol),
    price,
    selected,
    // Ticker 관련 필드
    ticker,
    fromHigh,
    fromLow,
    highLowChange,
    // Signal 관련 필드
    matchInfos: void 0,
    // 원본 데이터
    market
  };
}
__name(converToUnifiedRecord, "converToUnifiedRecord");
const getAnyPosition = /* @__PURE__ */ __name((status, exceptBackSignal = false) => {
  var _a, _b;
  const longPos = (_a = status.positions) == null ? void 0 : _a[PositionType.LONG];
  const shortPos = (_b = status.positions) == null ? void 0 : _b[PositionType.SHORT];
  if (exceptBackSignal) {
    return (longPos && longPos.orderMode !== OrderMode.BACK_SIGNAL ? longPos : void 0) || (shortPos && shortPos.orderMode !== OrderMode.BACK_SIGNAL ? shortPos : void 0);
  }
  return longPos || shortPos;
}, "getAnyPosition");
const getAnyOneWayPosition = /* @__PURE__ */ __name((status) => {
  var _a, _b, _c, _d, _e, _f;
  if (((_b = (_a = status.positions) == null ? void 0 : _a[PositionType.LONG]) == null ? void 0 : _b.hedged) === false) {
    return (_c = status.positions) == null ? void 0 : _c[PositionType.LONG];
  }
  if (((_e = (_d = status.positions) == null ? void 0 : _d[PositionType.SHORT]) == null ? void 0 : _e.hedged) === false) {
    return (_f = status.positions) == null ? void 0 : _f[PositionType.SHORT];
  }
  return void 0;
}, "getAnyOneWayPosition");
const getOppositePositionType = /* @__PURE__ */ __name((positionType) => positionType === PositionType.LONG ? PositionType.SHORT : PositionType.LONG, "getOppositePositionType");
const getOppositePosition = /* @__PURE__ */ __name((status, type) => {
  var _a;
  return (_a = status.positions) == null ? void 0 : _a[getOppositePositionType(type)];
}, "getOppositePosition");
function gcdNum(a, b) {
  let x = a;
  let y = b;
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}
__name(gcdNum, "gcdNum");
function getGcdMinTimeframe(timeframes) {
  const msValues = timeframes.map((tf) => getTimeframeMilliseconds(tf));
  let gcdMs = msValues[0];
  for (let i = 1; i < msValues.length; i++) {
    gcdMs = gcdNum(gcdMs, msValues[i]);
  }
  const result = AvailableTimeframes.find(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (tf) => getTimeframeMilliseconds(tf) === gcdMs
  );
  if (!result) throw new Error(`GCD ms ${gcdMs}\uB294 \uC720\uD6A8\uD55C \uD0C0\uC784\uD504\uB808\uC784\uC774 \uC544\uB2D9\uB2C8\uB2E4`);
  return result;
}
__name(getGcdMinTimeframe, "getGcdMinTimeframe");
export {
  calcPnl,
  calcRoe,
  converToUnifiedRecord,
  convertOhlcvToTicker,
  convertTickerToOhlcv,
  findDefaultOverlappingStep,
  getAnyOneWayPosition,
  getAnyPosition,
  getCandleComputedValue,
  getCandleValue,
  getEnterPositionOrderType,
  getExitPositionOrderType,
  getGcdMinTimeframe,
  getOhlcvIndex,
  getOppositePosition,
  getOppositePositionType,
  getOrderSide,
  getPositionType,
  getShortSymbolName,
  isEnterType,
  isEnterable,
  isExitType,
  isLongType,
  isMarketStatus,
  isPositionOrderType,
  isShortType,
  sortTimeframes,
  updateTickerPrice
};
//# sourceMappingURL=biz-utils.js.map
